/**
 * Normalizes a phone number by stripping all non-digit characters except leading +.
 * @param {string} phone - The raw phone number string.
 * @returns {string} Normalized phone number.
 */
export const normalizePhoneNumber = phone => {
  if (!phone) return '';
  const trimmed = phone.trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  return hasPlus ? `+${digits}` : digits;
};

// Matches phone numbers: optional +, then a digit, then 6+ chars of digits/separators, ending with a digit
const PHONE_REGEX = /(\+?\d[\d\-\(\)\s.]{6,}\d)/g;

/**
 * Processes an HTML string and wraps detected phone numbers in clickable spans.
 * Skips phone numbers already inside <a> tags.
 * @param {string} html - The HTML string to process.
 * @returns {string} HTML with phone numbers wrapped in clickable spans.
 */
export const wrapPhoneNumbers = html => {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html');

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach(textNode => {
    if (textNode.parentElement.closest('a')) return;

    const text = textNode.textContent;
    PHONE_REGEX.lastIndex = 0;
    if (!PHONE_REGEX.test(text)) return;
    PHONE_REGEX.lastIndex = 0;

    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    let match = PHONE_REGEX.exec(text);

    while (match !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(
          doc.createTextNode(text.slice(lastIndex, match.index))
        );
      }

      const span = doc.createElement('span');
      span.className = 'phone-link link underline cursor-pointer';
      span.dataset.phoneNumber = normalizePhoneNumber(match[1]);
      span.textContent = match[1];
      fragment.appendChild(span);

      lastIndex = match.index + match[0].length;
      match = PHONE_REGEX.exec(text);
    }

    if (lastIndex < text.length) {
      fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode.replaceChild(fragment, textNode);
  });

  return doc.body.innerHTML;
};

/**
 * Checks if two phone numbers match after normalization.
 * Handles cases like +972501234567 matching 0501234567 (Israeli format).
 * @param {string} phone1
 * @param {string} phone2
 * @returns {boolean}
 */
export const phoneNumbersMatch = (phone1, phone2) => {
  const n1 = normalizePhoneNumber(phone1);
  const n2 = normalizePhoneNumber(phone2);

  if (!n1 || !n2) return false;
  if (n1 === n2) return true;

  // Strip leading + for comparison
  const d1 = n1.replace(/^\+/, '');
  const d2 = n2.replace(/^\+/, '');
  if (d1 === d2) return true;

  // Handle Israeli format: +972X... matches 0X...
  // More generally: if one starts with country code and the other with 0
  if (d1.startsWith('972') && d2.startsWith('0')) {
    return d1.slice(3) === d2.slice(1);
  }
  if (d2.startsWith('972') && d1.startsWith('0')) {
    return d2.slice(3) === d1.slice(1);
  }

  // Check if one ends with the other (handles country code prefix differences)
  if (d1.length > 6 && d2.length > 6) {
    if (d1.endsWith(d2) || d2.endsWith(d1)) return true;
  }

  return false;
};
